/**
 * ServicingLoanDetail — Detail panel rendered inside the Sheet slide-over.
 *
 * Shows loan info, key dates, the amortization schedule, a P&L summary,
 * and a button to record payments.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { formatDateDisplay, getBusinessDateString, diffCalendarDays, msUntilNextBusinessMidnight } from '@/utils/dateHelpers';
import {
  Calendar,
  DollarSign,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  AlertCircle,
  Plus,
  Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { supabase } from '@/utils/supabase';
import { goodbyeFailureDescription, readGoodbyeSendResult } from '@/utils/goodbye-letter/sendResult';
import { useAuth } from '@/contexts/useAuth';
import { createComponentLogger } from '@/utils/logger';
import { formatCurrency } from '@/components/banker-recon/types';
import { useLoanOfficerServicingLoanDetail, useServicingLoanDetail } from '@/hooks/useServicing';
import { toNumber, needsPaymentReview, isSettledPaymentStatus, type ServicingLoan, type ServicingPayment, type PaymentReminderLoan } from '@/types/servicing';
import {
  getServicingFormUrl,
  checkServicingFormCache,
  sendPaymentReminders
} from '@/services/servicingService';
import { downloadAuthorizedGoodbyeLetterPdf } from '@/services/goodbyeLetterService';
import { GoodbyeLetterPrereqError } from '@/types/goodbye-letter';
import { describeGoodbyeSendResult, resolveGoodbyeLetterErrorMessage } from './goodbyeLetterErrors';
import { toast } from 'sonner';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { ReversePaymentDialog } from './ReversePaymentDialog';
import { PaymentReminderConfirmDialog } from './PaymentReminderConfirmDialog';
import { Form1098Tab } from './Form1098Tab';
import { downloadPaymentHistoryPdf, type PaymentHistoryData } from '@/utils/servicing/pdfGenerator';
import { REMINDER_WINDOW_DAYS, isWithinReminderWindow } from '../../../supabase/functions/_shared/servicing-reminder-window';

const log = createComponentLogger('ServicingLoanDetail', 'servicing');

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ServicingLoanDetailProps {
  servicingLoanId: string;
  /** For virtual rows (encompass_loans), pass the projected loan directly. */
  virtualLoan?: ServicingLoan | null;
  onPaymentRecorded?: () => Promise<void>;
  mode?: 'accounting' | 'loan-officer';
}

const VIRTUAL_LOAN_DISABLED_COPY = 'Servicing record has not been created yet';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get badge styling by payment status. */
function getPaymentBadge(
  payment: ServicingPayment,
  today: string
): { label: string; className: string; rowClassName: string } {
  if (payment.status === 'received') {
    return {
      label: 'Received',
      className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      rowClassName: 'bg-emerald-50/50'
    };
  }

  if (payment.status === 'bounced') {
    return {
      label: 'Bounced',
      className: 'bg-red-100 text-red-800 border-red-200',
      rowClassName: 'bg-red-50/50'
    };
  }

  if (payment.status === 'waived') {
    return {
      label: 'Waived',
      className: 'bg-slate-100 text-slate-600 border-slate-200',
      rowClassName: ''
    };
  }

  if (payment.status === 'partial') {
    return {
      label: 'Partial',
      className: 'bg-amber-100 text-amber-800 border-amber-200',
      rowClassName: 'bg-amber-50/50'
    };
  }

  // Check if overdue
  if (payment.due_date < today) {
    return {
      label: 'Overdue',
      className: 'bg-red-100 text-red-800 border-red-200',
      rowClassName: 'bg-red-50/50'
    };
  }

  // Due or scheduled
  if (payment.status === 'due') {
    return {
      label: 'Due',
      className: 'bg-amber-100 text-amber-800 border-amber-200',
      rowClassName: ''
    };
  }

  return {
    label: 'Scheduled',
    className: 'bg-slate-100 text-slate-500 border-slate-200',
    rowClassName: ''
  };
}

function formatReversalReason(reason: ServicingPayment['reversal_reason']): string | null {
  switch (reason) {
    case 'nsf':
      return 'NSF / bounced';
    case 'chargeback':
      return 'Chargeback';
    case 'bank_reject':
      return 'Bank rejected';
    case 'borrower_revoked':
      return 'Borrower revoked';
    case 'other':
      return 'Reversed';
    default:
      return null;
  }
}

function getReversalContext(payment: ServicingPayment): string | null {
  if (!payment.reversed_at) {
    return null;
  }

  const priorAmount = toNumber(payment.reversed_total_amount_received);
  const amountText = priorAmount > 0 ? formatCurrency(priorAmount) : null;
  const dateText = payment.reversed_payment_received_date
    ? formatDateDisplay(payment.reversed_payment_received_date)
    : null;
  const reasonText = formatReversalReason(payment.reversal_reason);

  if (payment.status === 'bounced') {
    return [amountText, dateText, reasonText].filter(Boolean).join(' • ');
  }

  return `Prior receipt reversed${reasonText ? ` • ${reasonText}` : ''}`;
}

/** Find the next payment number that hasn't been received. */
function getNextDuePaymentNumber(payments: ServicingPayment[]): number {
  for (const p of payments) {
    if (p.status !== 'received' && p.status !== 'waived') {
      return p.payment_number;
    }
  }
  // All received — return the count + 1 as fallback
  return payments.length > 0 ? payments[payments.length - 1].payment_number + 1 : 1;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ServicingLoanDetail({
  servicingLoanId,
  virtualLoan,
  onPaymentRecorded,
  mode = 'accounting'
}: ServicingLoanDetailProps) {
  const { user } = useAuth();
  const isVirtual = !!virtualLoan;
  const isLoanOfficerMode = mode === 'loan-officer';
  const accountingDetail = useServicingLoanDetail(!isVirtual && !isLoanOfficerMode ? servicingLoanId : null);
  const loanOfficerDetail = useLoanOfficerServicingLoanDetail(!isVirtual && isLoanOfficerMode ? servicingLoanId : null);
  const {
    loan: dbLoan,
    payments,
    form1098Data,
    loading,
    error,
    refresh,
    handleRecordPayment,
    handleReversePayment
  } = isLoanOfficerMode ? loanOfficerDetail : accountingDetail;

  // Virtual rows use the passed-in loan; DB rows use the hook result
  const loan = isVirtual ? virtualLoan : dbLoan;

  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [paymentToReverse, setPaymentToReverse] = useState<ServicingPayment | null>(null);
  const [generatingGoodbyeLetter, setGeneratingGoodbyeLetter] = useState(false);

  const canSendGoodbye = !isVirtual && !isLoanOfficerMode && user?.roles?.some(role => ['admin', 'super_admin'].includes(role.toLowerCase())) === true;
  const [goodbyeHistory, setGoodbyeHistory] = useState<{ loanId: string; status: 'ready' | 'error'; sentAt: string | null } | null>(null);
  const [sendingGoodbye, setSendingGoodbye] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!canSendGoodbye || !loan?.encompass_loan_id) {
      return;
    }
    const loanId = loan.id;
    void (async () => {
      try {
        const { data, error: historyError } = await supabase.from('loan_notification_facts')
          .select('sent_at').eq('encompass_loan_id', loan.encompass_loan_id)
          .eq('notification_type', 'servicing_goodbye_letter').not('sent_at', 'is', null)
          .order('sent_at', { ascending: false }).limit(1).maybeSingle();
        if (historyError) {
          throw historyError;
        }
        if (!cancelled) {
          setGoodbyeHistory({ loanId, status: 'ready', sentAt: data?.sent_at ?? null });
        }
      } catch (historyError) {
        log.warn('Could not check goodbye letter send history', { metadata: { loanId, error: historyError } });
        if (!cancelled) {
          setGoodbyeHistory({ loanId, status: 'error', sentAt: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canSendGoodbye, loan?.id, loan?.encompass_loan_id]);
  const currentGoodbyeHistory = goodbyeHistory?.loanId === loan?.id ? goodbyeHistory : null;

  const handleSendGoodbyeLetter = async (): Promise<void> => {
    if (!loan || !canSendGoodbye || sendingGoodbye || currentGoodbyeHistory?.status !== 'ready' || currentGoodbyeHistory.sentAt) {
      return;
    }
    const loanId = loan.id;
    setSendingGoodbye(true);
    try {
      const { data, error: sendError } = await supabase.functions.invoke('send-goodbye-letter', {
        body: { servicingLoanId: loanId, triggeredBy: 'manual_admin' }
      });
      const result = await readGoodbyeSendResult(data, sendError);
      if (result.success === true && !result.skipped) {
        setGoodbyeHistory({ loanId, status: 'ready', sentAt: new Date().toISOString() });
        toast.success('Goodbye letter sent');
      } else {
        log.warn('Goodbye letter manual send did not complete', {
          metadata: { servicingLoanId: loanId, error: result.error, reason: result.reason, code: result.code }
        });
        const outcome = describeGoodbyeSendResult(result);
        toast.error(
          outcome.state === 'unconfirmed' ? 'Goodbye letter delivery not confirmed' : 'Goodbye letter not sent',
          { description: goodbyeFailureDescription(outcome.text, result) }
        );
      }
    } catch (sendError) {
      // A thrown invocation proves nothing about delivery.
      log.warn('Goodbye letter manual send threw', { metadata: { servicingLoanId: loanId } }, sendError as Error);
      toast.error('Goodbye letter delivery not confirmed', {
        description: describeGoodbyeSendResult({ success: false, transportFailure: true }).text
      });
    } finally {
      setSendingGoodbye(false);
    }
  };

  const handleDownloadGoodbyeLetter = async (): Promise<void> => {
    // Loading-state / null-loan guard. Servicing detail can render
    // virtual or transitional rows; the toolbar button is conditional
    // already, but the handler also defends against in-flight re-entry.
    if (!loan || generatingGoodbyeLetter) {
      return;
    }
    setGeneratingGoodbyeLetter(true);
    try {
      await downloadAuthorizedGoodbyeLetterPdf(loan.id);

      // 5. Success toast.
      toast.success('Goodbye Letter downloaded', {
        description: `Loan ${loan.loan_number}`
      });
    } catch (e) {
      if (e instanceof GoodbyeLetterPrereqError) {
        const msg = resolveGoodbyeLetterErrorMessage(e);
        toast.error(msg.title, { description: msg.detail });
      } else {
        log.error('Goodbye Letter generation failed', { loanId: loan.id }, e as Error);
        toast.error('Could not generate Goodbye Letter', {
          description: (e as Error).message ?? 'Unexpected error'
        });
      }
    } finally {
      setGeneratingGoodbyeLetter(false);
    }
  };
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [formLoading, setFormLoading] = useState<Record<string, boolean>>({});
  const [formCached, setFormCached] = useState<Record<string, boolean>>({});
  const abortControllers = useRef<Record<string, AbortController>>({});

  // `today` must roll over at Pacific midnight, even if the detail view is
  // left open overnight — otherwise overdue badges and reminder age go stale.
  const [today, setToday] = useState<string>(() => getBusinessDateString());
  useEffect(() => {
    const handle = setTimeout(() => {
      setToday(getBusinessDateString());
    }, msUntilNextBusinessMidnight());
    return () => clearTimeout(handle);
  }, [today]);

  const nextPaymentNumber = useMemo(
    () => getNextDuePaymentNumber(payments),
    [payments]
  );

  const scheduledBalances = useMemo(() => {
    if (!loan) {
      return new Map<number, number>();
    }
    let balance = toNumber(loan.loan_amount);
    const map = new Map<number, number>();
    for (const p of payments) {
      balance -= toNumber(p.principal_due);
      map.set(p.payment_number, Math.round(balance * 100) / 100);
    }
    return map;
  }, [loan, payments]);

  // Check if forms are pre-cached in storage
  useEffect(() => {
    setFormCached({});
    if (isLoanOfficerMode) {
      return;
    }

    if (!loan?.encompass_loan_id) {
      return;
    }
    let cancelled = false;

    checkServicingFormCache(loan.encompass_loan_id, [
      'First Payment Letter',
      'Amortization Schedule'
    ]).then((result) => {
      if (!cancelled) {
        setFormCached(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isLoanOfficerMode, loan?.encompass_loan_id]);

  // P&L calculations from payment data
  const pnl = useMemo(() => {
    if (isLoanOfficerMode) {
      return {
        prepaidInterest: 0,
        totalPrincipalCollected: 0,
        totalInterestCollected: 0,
        totalEscrowCollected: 0,
        netServicingIncome: 0
      };
    }

    const prepaidInterest = toNumber(loan?.prepaid_interest);
    let totalPrincipalCollected = 0;
    let totalInterestCollected = 0;
    let totalEscrowCollected = 0;

    for (const p of payments) {
      totalPrincipalCollected += toNumber(p.principal_received);
      totalInterestCollected += toNumber(p.interest_received);
      totalEscrowCollected += toNumber(p.escrow_received);
    }

    return {
      prepaidInterest: Math.round(prepaidInterest * 100) / 100,
      totalPrincipalCollected: Math.round(totalPrincipalCollected * 100) / 100,
      totalInterestCollected: Math.round(totalInterestCollected * 100) / 100,
      totalEscrowCollected: Math.round(totalEscrowCollected * 100) / 100,
      netServicingIncome: Math.round((totalInterestCollected + prepaidInterest) * 100) / 100
    };
  }, [isLoanOfficerMode, loan, payments]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleViewForm = async (formName: string) => {
    if (!loan?.encompass_loan_id) {
      return;
    }

    // Abort any in-flight request for this form
    abortControllers.current[formName]?.abort();
    const controller = new AbortController();
    abortControllers.current[formName] = controller;

    // 120-second timeout
    const timeout = setTimeout(() => controller.abort(), 120_000);

    setFormLoading(prev => ({ ...prev, [formName]: true }));
    toast.info(`Generating ${formName}...`, { description: 'This may take up to 60 seconds.' });

    try {
      const result = await getServicingFormUrl(loan.encompass_loan_id, formName, controller.signal);
      if (result?.error) {
        toast.error(`Failed to generate ${formName}`, { description: result.error });
      } else if (result?.url) {
        window.open(result.url, '_blank');
        toast.success(`${formName} ready`, { description: 'PDF opened in a new tab.' });
        setFormCached(prev => ({ ...prev, [formName]: true }));
      } else {
        toast.error(`Failed to generate ${formName}`, { description: 'No response from server.' });
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        toast.error(`${formName} timed out`, { description: 'The request took too long. Please try again.' });
      } else {
        log.error('Form generation error', { formName, encompassLoanId: loan.encompass_loan_id }, e);
        toast.error(`Error generating ${formName}`, { description: e instanceof Error ? e.message : 'Unexpected error' });
      }
    } finally {
      clearTimeout(timeout);
      setFormLoading(prev => ({ ...prev, [formName]: false }));
    }
  };

  const handlePaymentSuccess = async () => {
    setRecordDialogOpen(false);
    await refresh();
    if (onPaymentRecorded) {
      await onPaymentRecorded();
    }
  };

  const handleReversePaymentSuccess = async () => {
    setPaymentToReverse(null);
    if (onPaymentRecorded) {
      await onPaymentRecorded();
    }
  };

  // Build a single PaymentReminderLoan from next due/overdue payment.
  // Depends on `today` so classification rolls over at Pacific midnight.
  const nextDuePayment = useMemo(() => {
    if (isVirtual || !loan) {
      return null;
    }
    const p = payments.find(pay => pay.status !== 'received' && pay.status !== 'waived');
    if (!p) {
      return null;
    }

    const isOverdue = p.due_date < today;
    const daysOverdue: number | undefined = isOverdue
      ? diffCalendarDays(p.due_date, today)
      : undefined;

    return {
      encompassLoanId: loan.encompass_loan_id,
      servicingLoanId: loan.id,
      loanNumber: loan.loan_number,
      borrowerName: loan.borrower_name || 'Unknown',
      amountDue: toNumber(p.total_amount_due),
      dueDate: p.due_date,
      paymentStatus: (isOverdue ? 'overdue' : 'upcoming') as const,
      daysOverdue
    } satisfies PaymentReminderLoan;
  }, [isVirtual, loan, payments, today]);

  const reminderWithinWindow =
    !!nextDuePayment && isWithinReminderWindow(nextDuePayment.dueDate, today);

  const handleSendReminder = () => {
    if (!nextDuePayment || !reminderWithinWindow) {
      return;
    }
    setReminderDialogOpen(true);
  };

  const handleDownloadPaymentHistory = () => {
    if (!loan || payments.length === 0) {
      return;
    }

    const historyData: PaymentHistoryData = {
      loanNumber: loan.loan_number,
      borrowerName: loan.borrower_name || '--',
      loanAmount: toNumber(loan.loan_amount),
      interestRate: toNumber(loan.interest_rate),
      termMonths: loan.loan_term_months,
      closingDate: loan.closing_date,
      firstPaymentDate: loan.first_payment_date,
      investorPurchaseDate: loan.investor_purchase_date,
      firstPaymentToInvestor: loan.first_payment_to_investor,
      investorName: loan.investor_name,
      investorLoanNumber: isLoanOfficerMode ? null : form1098Data?.investor_loan_number ?? null,
      propertyAddress: isLoanOfficerMode ? null : form1098Data?.property_address ?? null,
      propertyCity: isLoanOfficerMode ? null : form1098Data?.subject_property_city ?? null,
      propertyState: isLoanOfficerMode ? null : form1098Data?.subject_property_state ?? null,
      propertyZip: isLoanOfficerMode ? null : form1098Data?.property_zip ?? null,
      payments,
      nextPaymentDueDate: nextDuePayment?.dueDate ?? loan.next_payment_due_date,
      generatedDate: today
    };

    downloadPaymentHistoryPdf(historyData);
    toast.success('Payment History PDF downloaded.');
  };

  const handleConfirmReminder = async (selectedLoans: PaymentReminderLoan[]) => {
    if (selectedLoans.length === 0) {
      return;
    }
    setReminderSending(true);
    try {
      const result = await sendPaymentReminders(selectedLoans);
      if (!result.success) {
        toast.error(result.error || 'Failed to send reminder.');
        return;
      }
      const { sent = 0, skipped = 0, failed = 0 } = result.summary || {};
      if (sent > 0) {
        toast.success('Payment reminder sent to borrower.');
      } else if (skipped > 0) {
        const reason = result.results?.[0]?.reason;
        toast.warning(
          reason === 'already_sent'
            ? 'Reminder already sent for this upcoming payment.'
            : reason === 'no_borrower_email'
              ? 'No borrower email on file.'
              : `Reminder skipped: ${reason || 'unknown reason'}`
        );
      } else if (failed > 0) {
        toast.error(result.results?.[0]?.reason || 'Failed to send reminder.');
      }
    } catch (err) {
      log.error('Failed to send payment reminder', { error: err });
      toast.error('Failed to send payment reminder.');
    } finally {
      setReminderSending(false);
      setReminderDialogOpen(false);
    }
  };

  // -----------------------------------------------------------------------
  // Loading / Error states
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        Loading loan detail...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive mt-4">
        <AlertCircle className="h-4 w-4 shrink-0" />
        {error}
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        Loan not found
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="mt-6">
      {needsPaymentReview(loan) && (
        <div role="alert" className="mb-4 break-words rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
          <p className="font-semibold">Payment needs review</p>
          <p>Review the payment in Encompass and the closing documents, then synchronize the loan. Payment notices are blocked until the amounts agree.</p>
        </div>
      )}
      <Tabs defaultValue="summary">
        <TabsList>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          {!isLoanOfficerMode && <TabsTrigger value="1098">1098 Interest</TabsTrigger>}
        </TabsList>

        {/* ═══ Summary Tab ═══ */}
        <TabsContent value="summary" className="space-y-6 mt-4">
          {/* ─── Loan Info + Key Dates (side-by-side) ─── */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Loan Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                  <InfoRow label="Loan #" value={loan.loan_number} />
                  <InfoRow label="Borrower" value={loan.borrower_name || '--'} />
                  <InfoRow label="Loan Amount" value={formatCurrency(toNumber(loan.loan_amount))} />
                  <InfoRow label="Interest Rate" value={`${toNumber(loan.interest_rate).toFixed(3)}%`} />
                  <InfoRow
                    label="Term"
                    value={loan.loan_term_months ? `${loan.loan_term_months} months` : '--'}
                  />
                  {!isLoanOfficerMode && (
                    <InfoRow
                      label="Total Interest Paid"
                      value={formatCurrency(toNumber(loan.prepaid_interest) + toNumber(loan.total_interest_collected))}
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  <CardTitle className="text-sm">Key Dates</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                  <InfoRow label="Closing Date" value={formatDateDisplay(loan.closing_date)} />
                  <InfoRow label="Funded Date" value={formatDateDisplay(loan.funded_date)} />
                  <InfoRow
                    label="First Payment to Lendwise"
                    value={loan.first_payment_date && loan.first_payment_to_investor && loan.first_payment_date < loan.first_payment_to_investor
                      ? formatDateDisplay(loan.first_payment_date)
                      : 'N/A'}
                  />
                  <InfoRow label="Investor Purchase" value={formatDateDisplay(loan.investor_purchase_date)} />
                  <InfoRow label="First Payment to Investor" value={formatDateDisplay(loan.first_payment_to_investor)} />
                </div>

                <Separator />

                <div>
                  <h4 className="text-sm font-semibold mb-2">Investor Information</h4>
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                    <InfoRow label="Investor" value={loan.investor_name || '--'} />
                    <InfoRow label="Investor Loan #" value={form1098Data?.investor_loan_number || '--'} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ─── Amortization Table ─── */}
          <div>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Documents</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 whitespace-nowrap px-3 text-sm"
                    aria-label="Download Goodbye Letter"
                    onClick={() => void handleDownloadGoodbyeLetter()}
                    disabled={isVirtual || generatingGoodbyeLetter || needsPaymentReview(loan)}
                    title={isVirtual ? VIRTUAL_LOAN_DISABLED_COPY : undefined}
                  >
                    {generatingGoodbyeLetter ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-1" />
                    )}
                    Goodbye Letter
                  </Button>
                  {canSendGoodbye && (currentGoodbyeHistory?.sentAt ? (
                    <span className="self-center text-sm text-muted-foreground">Sent {formatDateDisplay(getBusinessDateString(new Date(currentGoodbyeHistory.sentAt)))}</span>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 whitespace-nowrap px-3 text-sm"
                      onClick={() => void handleSendGoodbyeLetter()}
                      disabled={sendingGoodbye || currentGoodbyeHistory?.status !== 'ready' || needsPaymentReview(loan)}
                      title={currentGoodbyeHistory?.status === 'error' ? 'Could not check send history. Reopen this loan to retry.' : undefined}
                    >
                      {sendingGoodbye && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                      Send goodbye letter
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 whitespace-nowrap px-3 text-sm"
                    onClick={handleDownloadPaymentHistory}
                    disabled={isVirtual || payments.length === 0 || needsPaymentReview(loan)}
                    title={
                      isVirtual ? VIRTUAL_LOAN_DISABLED_COPY
                      : payments.length === 0 ? 'No payment schedule generated'
                      : 'Download payment history PDF for investor'
                    }
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Payment History
                  </Button>
                  <Button
                    variant={formCached['First Payment Letter'] ? 'default' : 'outline'}
                    size="sm"
                    className="h-9 whitespace-nowrap px-3 text-sm"
                    aria-label="First Payment Letter"
                    onClick={() => handleViewForm('First Payment Letter')}
                    disabled={formLoading['First Payment Letter']}
                  >
                    {formLoading['First Payment Letter']
                      ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      : <FileText className="h-4 w-4 mr-1" />}
                    First Payment Letter
                  </Button>
                  <Button
                    variant={formCached['Amortization Schedule'] ? 'default' : 'outline'}
                    size="sm"
                    className="h-9 whitespace-nowrap px-3 text-sm"
                    aria-label="Amortization Schedule"
                    onClick={() => handleViewForm('Amortization Schedule')}
                    disabled={formLoading['Amortization Schedule']}
                  >
                    {formLoading['Amortization Schedule']
                      ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      : <FileText className="h-4 w-4 mr-1" />}
                    Amortization Schedule
                  </Button>
                </div>
              </div>

              {!isLoanOfficerMode && (
                <div className="min-w-0 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Servicing Actions</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 whitespace-nowrap px-3 text-sm"
                      onClick={handleSendReminder}
                      disabled={isVirtual || !nextDuePayment || !reminderWithinWindow || reminderSending || needsPaymentReview(loan)}
                      title={
                        isVirtual
                          ? VIRTUAL_LOAN_DISABLED_COPY
                          : !nextDuePayment
                            ? 'No pending payments to remind'
                            : !reminderWithinWindow
                              ? `Available ${REMINDER_WINDOW_DAYS} days before the due date`
                              : undefined
                      }
                    >
                      {reminderSending
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <Mail className="h-4 w-4 mr-1" />}
                      Send Reminder
                    </Button>
                    <Button
                      size="sm"
                      className="h-9 whitespace-nowrap px-3 text-sm"
                      aria-label="Record Payment"
                      onClick={() => setRecordDialogOpen(true)}
                      disabled={isVirtual || loan.status !== 'active' || needsPaymentReview(loan)}
                      title={
                        isVirtual ? VIRTUAL_LOAN_DISABLED_COPY
                        : loan.status !== 'active' ? `Loan is ${loan.status}`
                        : undefined
                      }
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Record Payment
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {payments.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center border rounded-md">
                No payment schedule generated yet
              </div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Principal</TableHead>
                      <TableHead className="text-right">Interest</TableHead>
                      <TableHead className="text-right">Escrow</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead>Date Received</TableHead>
                      {!isLoanOfficerMode && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => {
                      const badge = getPaymentBadge(payment, today);
                      const reversalContext = getReversalContext(payment);
                      return (
                        <TableRow key={payment.id} className={badge.rowClassName}>
                          <TableCell className="font-medium">
                            {payment.payment_number}
                          </TableCell>
                          <TableCell>{formatDateDisplay(payment.due_date)}</TableCell>
                          <TableCell className="text-right">
                            {needsPaymentReview(loan) && !isSettledPaymentStatus(payment.status) ? '--' : formatCurrency(toNumber(payment.principal_due))}
                          </TableCell>
                          <TableCell className="text-right">
                            {needsPaymentReview(loan) && !isSettledPaymentStatus(payment.status) ? '--' : formatCurrency(toNumber(payment.interest_due))}
                          </TableCell>
                          <TableCell className="text-right">
                            {needsPaymentReview(loan) && !isSettledPaymentStatus(payment.status) ? '--' : formatCurrency(toNumber(payment.escrow_due))}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {needsPaymentReview(loan) && !isSettledPaymentStatus(payment.status) ? 'Needs review' : formatCurrency(toNumber(payment.total_amount_due))}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(scheduledBalances.get(payment.payment_number) ?? 0)}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              <Badge variant="outline" className={badge.className}>
                                {badge.label}
                              </Badge>
                              {reversalContext && (
                                <span className="max-w-[180px] text-center text-xs text-muted-foreground">
                                  {reversalContext}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {toNumber(payment.total_amount_received) > 0
                              ? formatCurrency(toNumber(payment.total_amount_received))
                              : '--'}
                          </TableCell>
                          <TableCell>
                            {payment.payment_received_date
                              ? formatDateDisplay(payment.payment_received_date)
                              : '--'}
                          </TableCell>
                          {!isLoanOfficerMode && (
                            <TableCell className="text-right">
                              {payment.status === 'received' && !payment.reversed_at ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 whitespace-nowrap"
                                  onClick={() => setPaymentToReverse(payment)}
                                >
                                  Mark Bounced
                                </Button>
                              ) : (
                                <span className="text-xs text-muted-foreground">--</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {!isLoanOfficerMode && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">P&L Summary</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Prepaid Interest (at closing)</span>
                  <span className="font-medium">{formatCurrency(pnl.prepaidInterest)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Interest Collected (Revenue)</span>
                  <span className="font-medium text-emerald-700">{formatCurrency(pnl.totalInterestCollected)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Principal Collected</span>
                  <span className="font-medium">{formatCurrency(pnl.totalPrincipalCollected)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm font-semibold">
                  <span>Net Servicing Income</span>
                  <span className={pnl.netServicingIncome >= 0 ? 'text-emerald-700' : 'text-red-600'}>
                    {formatCurrency(pnl.netServicingIncome)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  Interest only. Principal reduces the balance passed to the investor.
                </p>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Escrow Collected</span>
                  <span className="font-medium">{formatCurrency(pnl.totalEscrowCollected)}</span>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  Escrow is a pass-through and is not included in servicing income.
                </p>
              </CardContent>
            </Card>
          )}

          {/* ─── Banker Recon Link ─── */}
          {!isLoanOfficerMode && loan.banker_recon_id && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                log.info('Navigate to banker recon', { bankerReconId: loan.banker_recon_id });
                window.open(`/accounting/banker-recon?id=${loan.banker_recon_id}`, '_blank');
              }}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Banker Recon
            </Button>
          )}
        </TabsContent>

        {/* ═══ 1098 Interest Tab ═══ */}
        {!isLoanOfficerMode && (
          <TabsContent value="1098">
            <Form1098Tab loan={loan} form1098Data={form1098Data} />
          </TabsContent>
        )}
      </Tabs>

      {/* ─── Record Payment Dialog (outside tabs) ─── */}
      {!isLoanOfficerMode && (
        <>
          <RecordPaymentDialog
            open={recordDialogOpen}
            onOpenChange={setRecordDialogOpen}
            servicingLoanId={servicingLoanId}
            nextPaymentNumber={nextPaymentNumber}
            payments={payments}
            onSuccess={handlePaymentSuccess}
            handleRecordPayment={handleRecordPayment}
            userId={user?.id}
          />

          <ReversePaymentDialog
            open={!!paymentToReverse}
            onOpenChange={(open) => {
              if (!open) {
setPaymentToReverse(null);
}
            }}
            payment={paymentToReverse}
            onSuccess={handleReversePaymentSuccess}
            handleReversePayment={handleReversePayment}
            userId={user?.id}
          />
        </>
      )}

      {/* Goodbye Letter is now one-click — see handleDownloadGoodbyeLetter
          above. No dialog mount is needed. */}

      {/* ─── Payment Reminder Confirm Dialog ─── */}
      {!isLoanOfficerMode && nextDuePayment && (
        <PaymentReminderConfirmDialog
          open={reminderDialogOpen}
          onOpenChange={setReminderDialogOpen}
          candidates={[nextDuePayment]}
          onConfirm={handleConfirmReminder}
          sending={reminderSending}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helper component for info rows
// ---------------------------------------------------------------------------

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </>
  );
}
